<script setup lang="ts">
/* eslint-disable no-console */
import { onMounted, ref, watch } from 'vue';
import { config } from '@/config';
import snsWebSdk from '@sumsub/websdk';
import { storeToRefs } from 'pinia';
import { useKycSessionStore } from '@/stores/kycSession.store';
import { alpha2ToAlpha3 } from 'i18n-iso-countries';
import type { Cardholder } from '@/models/Cardholder';
import { wellKnownKycSessionRoutes } from '@/routes/wellKnownKycSessionRoutes';
import type { ApplicantStatusChangedPayload } from '@/integrations/Sumsub/types';
import { redirectToTimeoutWithSessionId } from '@/util/timeoutRedirector';
import * as Sentry from '@sentry/vue';
import type { SnsError } from '@sumsub/websdk/types/types';
import { useRouter } from 'vue-router';
import Loading from '@/components/ui/Loading.vue';
import { wellKnownRoutes } from '@/routes/wellknown';
import { DocumentSubmissionMode } from '@/components/session/kyc/idv/DocumentSubmissionMode';
import KycSessionLayout from '@/layout/KycSessionLayout.vue';
import FlowNavigationButton from '../../ui/FlowNavigationButton.vue';
import { MockSnsWebSdk } from '@/integrations/Sumsub/MockSnsWebSdk';
import MockSumsubSdkContainer from '@/components/sumsub/MockSumsubSdkContainer.vue';
import { Environment } from '@/util/Environment';
import { ImmersveError } from '@/common/ImmersveError';

const SUMSUB_RETRY_LIMIT = 2;

const { kycSession } = storeToRefs(useKycSessionStore());
const router = useRouter();
const cardholder: Cardholder = kycSession.value?.cardholder as Cardholder;
const loading = ref(true);
const sumsubReloadCount = ref(0);

const currentDocumentSubmissionMode = ref<DocumentSubmissionMode>(DocumentSubmissionMode.Init);

//eslint-disable-next-line complexity
watch(currentDocumentSubmissionMode, () => {
  if (!currentDocumentSubmissionMode.value) {
    return;
  }
  switch (currentDocumentSubmissionMode.value.name) {
    case 'init':
      if (config.idvVendorDebugMode) {
        console.debug('Page State Event: Waiting for IDV to start');
      }
      break;
    case 'capture':
      if (config.idvVendorDebugMode) {
        console.debug('Page State Event: Waiting for documents');
      }
      break;
    case 'pending':
      if (config.idvVendorDebugMode) {
        console.debug('Page State Event: Waiting for results from IDV');
      }
      break;
    case 'reviewing':
    case 'approved':
      if (config.idvVendorDebugMode) {
        console.debug(
          'Page State Event: Redirecting to pending review page',
          currentDocumentSubmissionMode.value
        );
      }

      if (config.sumsubIdvRedirectEnabled) {
        router.push({ name: wellKnownKycSessionRoutes.PendingReview });
      }

      break;
    case 'blocked':
      if (config.idvVendorDebugMode) {
        console.debug('Page State Event: Submission Blocked');
      }
      break;
    default:
      if (config.idvVendorDebugMode) {
        console.debug('Page State Event: Unhandled state', currentDocumentSubmissionMode.value);
      }
      router.push({ name: wellKnownRoutes.Timeout });
      break;
  }
});

function selectWebSdk() {
  if (Environment.useInTestEnvironmentOtherwiseFalse(config.fakeIdvEnabled)) {
    return new MockSnsWebSdk({ component: MockSumsubSdkContainer });
  } else {
    return snsWebSdk;
  }
}

async function launchWebSdk(accessToken: string) {
  const contactChannels = await cardholder.loadContactChannels();
  const phoneParts = contactChannels.phone;
  const phone = phoneParts.prefix + phoneParts.number;
  const email = contactChannels.email?.emailAddress;
  const country = kycSession?.value?.region ? alpha2ToAlpha3(kycSession.value.region) : undefined;

  if (!kycSession.value) {
    throw new Error('Kyc session not found');
  }

  const snsWebSdkInstance = selectWebSdk()
    .init(accessToken, () => refreshExternalToken())
    .withConf({
      lang: navigator.language, //language of WebSDK texts and comments (ISO 639-1 format)
      country,
      email,
      phone,
      theme: 'light',
    })
    .withOptions({ addViewportTag: false, adaptIframeHeight: true })
    // see below what kind of messages WebSDK generates
    .on('idCheck.onApplicantStatusChanged', (payload) =>
      handleApplicantStatusChangedEvent(payload as ApplicantStatusChangedPayload)
    )
    .on('idCheck.onError', handleErrorEvent)
    .build();

  // you are ready to go:
  // just launch the WebSDK by providing the container element for it
  snsWebSdkInstance.launch('#sumsub-websdk-container');
}

async function refreshExternalToken() {
  if (!kycSession.value) {
    throw new Error('Kyc session not found');
  }
  return await kycSession.value.getExternalToken();
}

//See: https://docs.sumsub.com/reference/get-started-with-api
function handleApplicantStatusChangedEvent(payload?: ApplicantStatusChangedPayload) {
  if (config.idvVendorDebugMode) {
    console.debug('Sumsub Event: onApplicantStatusChanged', payload);
  }
  currentDocumentSubmissionMode.value = DocumentSubmissionMode.fromApplicantReviewResult(payload);
}

function isSumsubInitRetryable(error: SnsError) {
  return error.code === 'initialization-error' && sumsubReloadCount.value < SUMSUB_RETRY_LIMIT;
}

async function reloadSumsub() {
  loading.value = true;
  const token = await refreshExternalToken();
  await launchWebSdk(token);
}

async function handleErrorEvent(error: SnsError) {
  //eslint-disable-next-line no-console
  console.debug('onError', error);

  if (isSumsubInitRetryable(error)) {
    sumsubReloadCount.value++;
    Sentry.captureMessage('Sumsub SDK initialization-error. Retrying...', {
      extra: { error, reloadAttempt: sumsubReloadCount.value },
    });
    return await reloadSumsub();
  }

  const sumsubError = ImmersveError.fromSumsub(error);
  Sentry.captureException(sumsubError, {
    contexts: {
      sumsub: {
        ...error,
      },
    },
  });

  await redirectToTimeout();
}

async function redirectToTimeout() {
  await router.push(redirectToTimeoutWithSessionId({ kycSession: kycSession.value }));
}

onMounted(async () => {
  if (!kycSession.value?.region) {
    throw new Error('Kyc session region not found');
  }

  const consentedForRegion = kycSession.value.cardholder.hasConsentForRegion({
    kycSessionId: kycSession.value.id,
    region: kycSession.value.region,
  });

  if (!consentedForRegion) {
    return await router.replace({
      name: wellKnownKycSessionRoutes.Consent,
    });
  }

  if (!(await kycSession.value.cardholder.hasResidentialAddress())) {
    Sentry.captureMessage(`No residential address found for cardholder ${cardholder.accountId}`);
    return await router.replace({
      name: wellKnownKycSessionRoutes.Address,
    });
  }
  await kycSession.value?.pushExternalProfile();
  const token = await kycSession.value.getExternalToken();
  await launchWebSdk(token);
});

defineExpose({
  handleApplicantStatusChangedEvent,
  handleErrorEvent,
  documentSubmissionStatus: currentDocumentSubmissionMode,
});
</script>

<template>
  <KycSessionLayout>
    <div id="sumsub-websdk-container">
      <Loading class="mx-auto mt-10 h-10 w-10" v-if="loading" />
    </div>
    <template #button-bar>
      <FlowNavigationButton text="Back" direction="back" />
    </template>
  </KycSessionLayout>
</template>

<style>
@media screen and (min-width: 500px) {
  #sumsub-websdk-container > iframe {
    width: 500px;
    margin-left: auto;
    margin-right: auto;
  }
}

@media screen and (max-width: 500px) {
  #sumsub-websdk-container > iframe {
    width: 100%;
  }
}
</style>
